/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from '@vlinder-web/plugin-agent-service-react';
import { eventTimelinePayoutListConfig } from '../../config/eventTimeline.config';
import { GET_EVENT_TIMELINE } from '@pages/PayoutListPage/apis/graphql';

import { Graphql } from '@common/graphql';
import { apiConfig } from '@pages/PayoutListPage/config/api.config';
import { vappContainer } from '@/application';
import { AppNavigatorService } from '@/AppNavigator/services';
import { useEventId } from '@pages/PayoutListPage/provider';
import { toHeaderCase } from 'js-convert-case';
import moment from 'moment';

const loginSrv = () => {
  return vappContainer.get<AppNavigatorService>(
    AppNavigatorService.displayName
  );
};

export interface IEventTimelineMethods extends IPluginMethodMap {
  getEventTimelineProps(args: any): Promise<any>;
}

export class EventTimelinePlugin implements IAgentPlugin {
  readonly methods: IEventTimelineMethods;

  constructor() {
    this.methods = {
      getEventTimelineProps: this.getProps.bind(this),
    };
  }

  async getProps(args: any) {
    //return await this.getEventTimeline(args);
    return { ...eventTimelinePayoutListConfig };
  }

  private async getEventTimeline(args: any) {
    const { eventId } = useEventId();
    try {
      let gql = Graphql.withBaseUrl(apiConfig.gql.url);
      // const token = getToken();
      const token = loginSrv()?.getToken();
      if (token) {
        gql = gql.withHeaders({
          authentication: token,
        });
      }
      const client = gql.getClient();
      const res = await client
        .query(GET_EVENT_TIMELINE, {
          input: {
            where: [
              {
                column: 'txnSetId',
                value: eventId,
                op: 'Equal',
              },
              {
                column: 'type',
                value: 'CYCLONE_OCCURRENCE',
                op: 'Equal',
              },
            ],
          },
        })
        .toPromise();
      if (res?.data?.getTxnList) {
        console.log(`event transaction details`, res?.data?.getTxnList);
        return {
          timeline: this.getTimeLine(res?.data?.getTxnList?.data),
        };
      }
    } catch (err: any) {
      throw err;
    }
  }

  private getTimeLine(data: any) {
    let _temp: any = [];
    Array.isArray(data) &&
      data?.forEach((element) => {
        _temp.push({
          key: element?.id,
          headline: toHeaderCase(element?.action ?? ''),
          headlineBackgroundColor: '#579783',
          children: this.getChildren(element),
        });
      });
    return _temp;
  }

  private getChildren(data: any) {
    let _temp: any = [];
    _temp.push({
      itemId: data?.id,
      title: toHeaderCase(
        data?.payload?.stormName
          ? data?.payload?.stormName
          : data?.payload?.payoutEstimate
          ? 'Payout Estimate'
          : data?.payload?.status ?? '-'
      ),
      multiColList: this.getProvenanceAccordion(data),
      subtitle: data?.payload?.date
        ? moment(data?.payload?.date).format('LLL')
        : data?.payload?.payoutEstimate ?? null,
    });
    return _temp;
  }

  private getProvenanceAccordion(res: any) {
    let _temp: any = [];
    if (res) {
      let _list: any = [];
      let _date: any = [];
      let _location: any = [];
      if (res?.payload?.category) {
        _list.push({
          title: toHeaderCase('storm strength'),
          subTitle: `${toHeaderCase(res?.payload?.category)}`,
          icon: eventTimelinePayoutListConfig?.category,
        });
      }
      if (res?.payload?.exposure) {
        _location.push({
          title: toHeaderCase('exposer'),
          subTitle: toHeaderCase(res?.payload?.exposure) ?? '-',
          icon: eventTimelinePayoutListConfig?.exposure,
        });
      }
      if (res?.payload?.vmax) {
        _date.push({
          title: toHeaderCase('Max. wind speed'),
          subTitle: res?.payload?.vmax ? `${res?.payload?.vmax} m/s` : '-',
          icon: eventTimelinePayoutListConfig?.wind,
        });
      }
      if (res?.payload?.vmin) {
        _date.push({
          title: toHeaderCase('on'),
          subTitle: res?.payload?.vmin ? `${res?.payload?.vmin} m/s` : '-',
          icon: eventTimelinePayoutListConfig?.wind,
        });
      }
      if (res?.payload?.totalPolicies) {
        _date.push({
          title: toHeaderCase('Eligible Policies'),
          subTitle: res?.payload?.totalPolicies,
          icon: eventTimelinePayoutListConfig?.user,
        });
      }
      if (res?.performedBy) {
        _date.push({
          title: toHeaderCase('by'),
          subTitle: `${toHeaderCase(
            res?.performedBy?.firstName ?? ''
          )} ${toHeaderCase(res?.performedBy?.lastName ?? '')}`,
          icon: eventTimelinePayoutListConfig?.orgIcon,
        });
      }
      _temp = [..._list, ..._location, ..._date];
    }
    return {
      numberOfColumns: '2',
      data: _temp,
    };
  }
}
